// Linke Navigation, ruhig und schmal: Inbox (Zähler), Leads, Stufe-1-Bereiche, darunter nur die eigenen
// angehefteten Smart Views (Pins aus Close plus im CRM angeheftete; ohne Pins die eigenen), höchstens zehn,
// der Rest hinter „Mehr“. Alle, geteilte und fremde Smart Views stehen auf eigenen Seiten
// (/smart-views/alle|eigene|geteilt|andere), erreichbar über „…“ bzw. Rechtsklick auf die Überschrift und über
// „Alle Smart Views“, das immer sichtbar unter der Liste steht. Fußzeile: Profil, Telefon-Zustand, Einstellungen.
// Runde 5 (Agent 5B): Seitenleiste einklappbar auf eine Symbolleiste (Knopf im Kopf oder ⌘S bzw. Strg+S, gemerkt je Browser), Lupe an
// „Angeheftet“ öffnet die Suche über alle Smart Views, eingeklappt öffnet das Stecknadel-Symbol die angehefteten.
import { useCallback, useEffect, useMemo, useState, type MouseEvent } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import { aufgabenApi, type SmartViewKurz } from "../api";
import { useEchtzeit } from "../api/echtzeit";
import { useAuth } from "../auth/AuthContext";
import { BackofficeLink } from "./BackofficeLink";
import { listeLesen } from "../daten/liste";
import { useMeta } from "../daten/meta";
import { angeheftete, anheften, istTrenner, kurzName, useSmartViews } from "../daten/smartviews";
import { useNeuVerbunden } from "../daten/verbindung";
import { useTelefonie } from "../telefonie";
import { T } from "../texte";
import { Avatar } from "./Bausteine";
import { Icon, Logo } from "./Icon";
import { KontextMenue, MenueKnopf, type MenuePunkt } from "./Menue";
import { useMeldung } from "./Meldungen";
import { SmartViewDialoge } from "./smartview/DialogeHost";
import { SmartViewWechsler } from "./smartview/BedienleisteWechsler";
import "./NavigationEinklappen.css";
import { neuerLeadOeffnen } from "./smartview/dialogBus";

const OHNE_PINS_HOECHSTENS = 12;
/** So viele Smart Views (ohne Trennlinien) stehen in der Seitenleiste, der Rest hinter „Mehr“. */
const SICHTBAR_HOECHSTENS = 10;
const MEHR_SCHLUESSEL = "crm-nav-mehr";
const ZU_SCHLUESSEL = "crm-nav-zu";

function zuGemerkt(): boolean {
  try {
    return localStorage.getItem(ZU_SCHLUESSEL) === "1";
  } catch {
    return false;
  }
}

/** Symbol „Seitenleiste“: Rahmen mit abgesetzter linker Spalte. */
function SeitenleisteSymbol({ groesse = 16 }: { groesse?: number }) {
  return (
    <svg width={groesse} height={groesse} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.8} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d="M4 5h16v14H4zM9.5 5v14" />
    </svg>
  );
}

function mehrGemerkt(): boolean {
  try {
    return localStorage.getItem(MEHR_SCHLUESSEL) === "1";
  } catch {
    return false;
  }
}

export function Navigation({ onTasten }: { onTasten: () => void }) {
  const { nutzer, abmelden } = useAuth();
  const { meta } = useMeta();
  const ort = useLocation();
  const navigate = useNavigate();
  const meldung = useMeldung();
  const [inbox, setInbox] = useState<{ inbox: number; overdue: number } | null>(null);
  const { views, neuLaden } = useSmartViews();
  const [kontext, setKontext] = useState<{ x: number; y: number; punkte: MenuePunkt[] } | null>(null);
  const [mehrOffen, setMehrOffen] = useState(mehrGemerkt);
  const [zu, setZu] = useState(zuGemerkt);
  const [wechslerAnker, setWechslerAnker] = useState<HTMLElement | null>(null);
  const [wechslerOffen, setWechslerOffen] = useState(false);

  // Einklappen: die Breite der Seitenleiste folgt dem Merkmal am Wurzelelement (siehe NavigationEinklappen.css)
  useEffect(() => {
    const wurzel = document.documentElement;
    if (zu) wurzel.dataset.navZu = "1";
    else delete wurzel.dataset.navZu;
    return () => {
      delete wurzel.dataset.navZu;
    };
  }, [zu]);
  const zuUmschalten = useCallback(() => {
    setZu((alt) => {
      const neu = !alt;
      try {
        localStorage.setItem(ZU_SCHLUESSEL, neu ? "1" : "0");
      } catch {
        /* ohne Speicher nur für diese Sitzung */
      }
      return neu;
    });
  }, []);
  // ⌘S bzw. Strg+S klappt die Seitenleiste ein und aus (wirkt auch beim Tippen; die Browser-Funktion „Seite speichern“ bleibt aus)
  useEffect(() => {
    const taste = (e: KeyboardEvent) => {
      if (e.defaultPrevented || e.isComposing || !(e.metaKey || e.ctrlKey) || e.shiftKey || e.altKey || e.key.toLowerCase() !== "s") return;
      e.preventDefault();
      zuUmschalten();
    };
    window.addEventListener("keydown", taste);
    return () => window.removeEventListener("keydown", taste);
  }, [zuUmschalten]);

  const zaehlerLaden = useCallback(() => {
    aufgabenApi
      .zaehler()
      .then((z) => {
        setInbox({ inbox: z.inbox, overdue: z.overdue });
        // Desktop-App: Zahl am Dock-Symbol
        void Promise.resolve(window.crmDesktop?.abzeichen(z.inbox)).catch(() => undefined);
      })
      .catch(() => setInbox(null));
  }, []);

  useEffect(() => {
    zaehlerLaden();
    const t = window.setInterval(zaehlerLaden, 5 * 60_000);
    return () => window.clearInterval(t);
  }, [zaehlerLaden]);

  useEchtzeit(["task.created", "task.updated"], zaehlerLaden);
  // Runde 5: Smart View gespeichert, geändert oder gelöscht (Agent 5A): Liste der Seitenleiste neu holen
  useEchtzeit(["smart_view.updated"], () => void neuLaden());
  useNeuVerbunden(() => {
    zaehlerLaden();
    void neuLaden();
  });

  // Fällt die Liste aus, die Smart Views aus /api/meta zeigen.
  const liste = views ?? meta?.smart_views ?? [];
  const eigeneIds = useMemo(() => new Set([nutzer?.id, nutzer?.close_user_id].filter(Boolean) as string[]), [nutzer]);
  const pins = useMemo(() => angeheftete(liste), [liste]);
  const ohnePins = pins.length === 0;
  const seitenleiste = useMemo(() => {
    if (!ohnePins) return pins;
    return liste
      .filter((v) => v.owner_id && eigeneIds.has(v.owner_id) && !istTrenner(v.name))
      .sort((a, b) => a.name.localeCompare(b.name, "de"))
      .slice(0, OHNE_PINS_HOECHSTENS);
  }, [ohnePins, pins, liste, eigeneIds]);

  const aktuelleView = /^\/smart-views\/(?!alle$|eigene$|geteilt$|andere$)([^/]+)/.exec(ort.pathname)?.[1];
  const herkunft = ort.pathname.startsWith("/lead/") ? listeLesen() : null;
  const herkunftView = herkunft?.quelle === "smartview" ? herkunft.pfad : null;

  // Höchstens zehn Views sichtbar (Trennlinien zählen nicht, keine Linie am Ende); die gerade offene bzw. die,
  // aus der der Lead kommt, bleibt auch eingeklappt sichtbar.
  const { sichtbar, rest, restZahl } = useMemo(() => {
    const vorn: SmartViewKurz[] = [];
    const hinten: SmartViewKurz[] = [];
    let echte = 0;
    for (const v of seitenleiste) {
      if (echte >= SICHTBAR_HOECHSTENS) hinten.push(v);
      else {
        vorn.push(v);
        if (!istTrenner(v.name)) echte++;
      }
    }
    while (vorn.length && istTrenner(vorn[vorn.length - 1].name)) hinten.unshift(vorn.pop()!);
    return { sichtbar: vorn, rest: hinten, restZahl: hinten.filter((v) => !istTrenner(v.name)).length };
  }, [seitenleiste]);
  const offenePfade = new Set([ort.pathname, herkunftView].filter(Boolean) as string[]);
  const eingeklapptAktiv = mehrOffen ? [] : rest.filter((v) => !istTrenner(v.name) && offenePfade.has(`/smart-views/${v.id}`));
  const mehrUmschalten = () => {
    const neu = !mehrOffen;
    setMehrOffen(neu);
    try {
      localStorage.setItem(MEHR_SCHLUESSEL, neu ? "1" : "0");
    } catch {
      /* ohne Speicher nur für diese Sitzung */
    }
  };

  const uebersichten: MenuePunkt[] = [
    { label: T.smartViews.alleLang, icon: "felder", onClick: () => navigate("/smart-views/alle") },
    { label: T.smartViews.eigeneLang, icon: "person", onClick: () => navigate("/smart-views/eigene") },
    { label: T.smartViews.geteiltLang, icon: "link", onClick: () => navigate("/smart-views/geteilt") },
    { label: T.smartViews.andereLang, icon: "ansicht", onClick: () => navigate("/smart-views/andere") },
  ];

  const pinAendern = (v: SmartViewKurz, an: boolean) => {
    anheften(v.id, an).catch((e: unknown) => meldung.zeige("fehler", e instanceof Error ? e.message : "Anheften hat nicht geklappt."));
  };

  const viewMenue = (e: MouseEvent, v: SmartViewKurz) => {
    e.preventDefault();
    setKontext({
      x: e.clientX,
      y: e.clientY,
      punkte: [
        { label: v.name, ueberschrift: true },
        { label: T.smartViews.oeffnen, icon: "pfeil-rechts", onClick: () => navigate(`/smart-views/${v.id}`) },
        { label: T.smartViews.neuerTab, icon: "extern", onClick: () => window.open(`/smart-views/${v.id}`, "_blank", "noopener") },
        v.pinned
          ? { label: T.smartViews.loesen, icon: "stecknadel", onClick: () => pinAendern(v, false) }
          : { label: T.smartViews.anheften, icon: "stecknadel", onClick: () => pinAendern(v, true) },
        { ...uebersichten[0], trenner: true },
      ],
    });
  };

  const viewPunkt = (v: SmartViewKurz) =>
    istTrenner(v.name) ? (
      <div key={v.id} className="nav__trenner" role="separator" onContextMenu={(e) => viewMenue(e, v)} />
    ) : (
      <div key={v.id} className="nav__view" onContextMenu={(e) => viewMenue(e, v)}>
        <NavLink
          to={`/smart-views/${v.id}`}
          className={`nav__punkt nav__punkt--view${herkunftView === `/smart-views/${v.id}` ? " nav__punkt--herkunft" : ""}`}
          title={v.name}
        >
          <span className="nav__text">{kurzName(v.name, nutzer?.first_name)}</span>
        </NavLink>
        {v.pinned ? (
          <button type="button" className="nav__loesen" title={T.smartViews.loesen} aria-label={`${T.smartViews.loesen}: ${v.name}`} onClick={() => pinAendern(v, false)}>
            <Icon name="schliessen" groesse={12} />
          </button>
        ) : null}
      </div>
    );

  return (
    <nav className={`nav${zu ? " nav--zu" : ""}`} aria-label="Hauptnavigation">
      <div className="nav__kopf">
        <Logo groesse={20} />
        <span className="nav__text nav__marke">{T.app}</span>
        <button
          type="button"
          className="nav__mini nav__klappe"
          title={zu ? "Seitenleiste ausklappen (⌘S)" : "Seitenleiste einklappen (⌘S)"}
          aria-label={zu ? "Seitenleiste ausklappen" : "Seitenleiste einklappen"}
          aria-pressed={zu}
          onClick={zuUmschalten}
        >
          <SeitenleisteSymbol />
        </button>
      </div>
      <div className="nav__liste">
        <NavLink to="/inbox" className="nav__punkt" title={zu ? T.nav.inbox : undefined}>
          <Icon name="inbox" groesse={16} />
          <span className="nav__text">{T.nav.inbox}</span>
          {inbox && inbox.inbox > 0 ? (
            <span className={`nav__zahl${inbox.overdue > 0 ? " nav__zahl--ueberfaellig" : ""}`} title={inbox.overdue ? `${inbox.overdue} überfällig` : undefined}>
              {inbox.inbox}
            </span>
          ) : null}
        </NavLink>
        <div className="nav__view">
          <NavLink to="/leads" end className="nav__punkt" title={zu ? T.nav.leads : undefined}>
            <Icon name="leads" groesse={16} />
            <span className="nav__text">{T.nav.leads}</span>
          </NavLink>
          <button type="button" className="nav__loesen nav__neu" title="Neuer Lead (⌘⇧L)" aria-label="Neuer Lead" onClick={() => neuerLeadOeffnen()}>
            <Icon name="plus" groesse={12} />
          </button>
        </div>
        <NavLink to="/kontakte" className="nav__punkt" title={zu ? T.nav.kontakte : undefined}>
          <Icon name="kontakte" groesse={16} />
          <span className="nav__text">{T.nav.kontakte}</span>
        </NavLink>
        <NavLink to="/aktivitaeten" className="nav__punkt" title={zu ? T.nav.aktivitaeten : undefined}>
          <Icon name="aktivitaet" groesse={16} />
          <span className="nav__text">{T.nav.aktivitaeten}</span>
        </NavLink>
        <NavLink to="/opportunities" className="nav__punkt" title={zu ? T.nav.opportunities : undefined}>
          <Icon name="opportunity" groesse={16} />
          <span className="nav__text">{T.nav.opportunities}</span>
        </NavLink>
        <NavLink to="/reports" className="nav__punkt" title={zu ? T.nav.reports : undefined}>
          <Icon name="report" groesse={16} />
          <span className="nav__text">{T.nav.reports}</span>
        </NavLink>
        <NavLink to="/gespraeche" className="nav__punkt" title={zu ? T.nav.gespraeche : undefined}>
          <Icon name="telefon" groesse={16} />
          <span className="nav__text">{T.nav.gespraeche}</span>
        </NavLink>
        <NavLink to="/workflows" className="nav__punkt" title={zu ? T.nav.workflows : undefined}>
          <Icon name="blitz" groesse={16} />
          <span className="nav__text">{T.nav.workflows}</span>
        </NavLink>
        {nutzer?.permissions.includes("leads_zusammenfuehren") ? (
          <NavLink to="/dubletten" className="nav__punkt" title={zu ? T.nav.dubletten : undefined}>
            <Icon name="zusammen" groesse={16} />
            <span className="nav__text">{T.nav.dubletten}</span>
          </NavLink>
        ) : null}
      </div>
      <div
        className="nav__abschnitt"
        onContextMenu={(e) => {
          e.preventDefault();
          setKontext({ x: e.clientX, y: e.clientY, punkte: uebersichten });
        }}
        title="Rechtsklick: alle, eigene, geteilte Smart Views und die von anderen"
      >
        <span>{T.nav.smartViews}</span>
        <span className="nav__abschnitt-knoepfe">
          <button
            type="button"
            className="nav__mini"
            title="Smart View suchen"
            aria-label="Smart View suchen"
            aria-haspopup="listbox"
            aria-expanded={wechslerOffen}
            onClick={(e) => {
              setWechslerAnker(e.currentTarget);
              setWechslerOffen((o) => !o);
            }}
          >
            <Icon name="suche" groesse={14} />
          </button>
          <MenueKnopf knopfKlasse="nav__mini" titel="Smart-View-Listen" punkte={uebersichten} ausrichtung="start">
            <Icon name="mehr" groesse={14} />
          </MenueKnopf>
        </span>
      </div>
      {/* eingeklappt: ein Stecknadel-Symbol statt der Liste, es öffnet die Suche über alle Smart Views */}
      <div className="nav__liste nav__liste--zu">
        <button
          type="button"
          className="nav__punkt"
          title="Smart Views (angeheftet und alle)"
          aria-label="Smart Views suchen"
          onClick={(e) => {
            setWechslerAnker(e.currentTarget);
            setWechslerOffen((o) => !o);
          }}
        >
          <Icon name="stecknadel" groesse={16} />
        </button>
      </div>
      <SmartViewWechsler anker={wechslerAnker} offen={wechslerOffen} onSchliessen={() => setWechslerOffen(false)} aktuell={aktuelleView ? decodeURIComponent(aktuelleView) : "leads"} />
      <div className="nav__views">
        <div className="nav__liste nav__liste--views">
          {liste.length === 0 ? (
            <div className="nav__leer">{views === null && !meta ? T.laden : T.nav.keineViews}</div>
          ) : (
            <>
              {sichtbar.map(viewPunkt)}
              {eingeklapptAktiv.map(viewPunkt)}
              {mehrOffen ? rest.map(viewPunkt) : null}
              {restZahl > 0 ? (
                <button type="button" className="nav__mehr" aria-expanded={mehrOffen} onClick={mehrUmschalten}>
                  <Icon name={mehrOffen ? "chevron-hoch" : "chevron-unten"} groesse={12} />
                  {mehrOffen ? T.nav.weniger : `${T.nav.mehr} (${restZahl})`}
                </button>
              ) : null}
              {ohnePins && seitenleiste.length === 0 ? <div className="nav__leer">{T.nav.keinePins}</div> : null}
            </>
          )}
        </div>
      </div>
      {liste.length > 0 ? (
        <div className="nav__alle-rahmen">
          <NavLink to="/smart-views/alle" className="nav__alle">
            <Icon name="felder" groesse={14} />
            {T.nav.alleZeigen}
            <span className="nav__alle-zahl">{liste.filter((v) => !istTrenner(v.name)).length}</span>
          </NavLink>
        </div>
      ) : null}
      <div className="nav__luft" />
      <div className="nav__fuss">
        <MenueKnopf
          knopfKlasse="nav__punkt nav__profil"
          titel="Profil"
          punkte={[
            { label: nutzer?.email ?? nutzer?.name ?? "", ueberschrift: true },
            { label: T.nav.tasten, icon: "tastatur", onClick: onTasten, rechts: <kbd>?</kbd> },
            { label: T.nav.abmelden, icon: "abmelden", trenner: true, onClick: () => void abmelden() },
          ]}
        >
          <Avatar name={nutzer?.name} id={nutzer?.id} groesse={20} />
          <span className="nav__text">{nutzer?.name}</span>
        </MenueKnopf>
        <BackofficeLink />
        <TelefonKnopf />
        <NavLink to="/einstellungen" className="nav__mini nav__mini--fuss" title={T.nav.einstellungen} aria-label={T.nav.einstellungen}>
          <Icon name="einstellungen" groesse={16} />
        </NavLink>
      </div>
      <KontextMenue ort={kontext} punkte={kontext?.punkte ?? []} onSchliessen={() => setKontext(null)} />
      <SmartViewDialoge />
    </nav>
  );
}

/**
 * Telefon in der Fußzeile: ruhiger Zustand (nicht eingerichtet, bereit) steht nur hier als Punkt am Symbol,
 * statt als schwebende Pille über dem Inhalt. Klick öffnet bzw. schließt das Telefon-Feld.
 */
function TelefonKnopf() {
  const t = useTelefonie();
  const navigate = useNavigate();
  if (!t.info && t.status === "nicht_eingerichtet") return null; // Telefonie noch nicht geladen
  // Ein vergessenes „Nicht stören“ schickt sonst still alle Anrufe in die Mailbox: sichtbar machen (nur lesen, was die Telefonie meldet)
  const betriebsbereit = t.status !== "fehler" && !t.fehler && t.status !== "nicht_eingerichtet";
  const nichtErreichbar = betriebsbereit && t.info?.erreichbar === false;
  const stoerHinweis = nichtErreichbar ? (t.info?.nicht_stoeren ? "Nicht stören" : "außerhalb der Arbeitszeit") : null;
  const punkt =
    t.status === "fehler" || t.fehler
      ? "rot"
      : t.status === "nicht_eingerichtet" || t.status === "initialisiert"
        ? "grau"
        : (t.info?.hinweise?.length ?? 0) > 0 || nichtErreichbar
          ? "gelb"
          : "gruen";
  const text =
    t.status === "nicht_eingerichtet"
      ? t.info?.enabled
        ? "Telefon: keine Absendernummer"
        : "Telefonie nicht eingerichtet"
      : t.status === "fehler"
        ? `Telefon: ${t.fehler ?? "Störung"}`
        : stoerHinweis
          ? `Telefon: ${stoerHinweis}`
          : `Telefon ${punkt === "gelb" ? "mit Hinweis" : "bereit"}`;
  return (
    <>
      {stoerHinweis ? (
        <button
          type="button"
          className="nav__telefon-hinweis"
          title={`${stoerHinweis}: Anrufe klingeln nicht bei dir. Klick: Einstellungen › Telefonie`}
          aria-label={`Telefon ${stoerHinweis}, Einstellungen Telefonie öffnen`}
          onClick={() => navigate("/einstellungen/telefonie")}
        >
          {stoerHinweis}
        </button>
      ) : null}
      <button
        type="button"
        className={`nav__mini nav__mini--fuss nav__telefon${t.panelOffen ? " active" : ""}`}
        title={`${t.grund ?? text} · Klick: Telefon-Leiste`}
        aria-label={text}
        aria-pressed={t.panelOffen}
        onClick={() => t.setPanelOffen(!t.panelOffen)}
      >
        <Icon name="telefon" groesse={15} />
        <span className={`nav__telefon-punkt nav__telefon-punkt--${punkt}`} aria-hidden="true" />
      </button>
    </>
  );
}
